import React, { useEffect, useState } from "react";
import { Tooltip, Badge, Button } from "@src/common/components";
import { Table } from "@src/common/components/Table/Table";
import classNames from "classnames";
import {
  DispatchableType,
  OrderIdentifierColumnType,
  QualityReviewVerdict,
  RecurrenceType,
  ShipmentsSortField,
  StopCompletionReason,
} from "@api/graphql/generated/generated-types";
import { PackageTooltip } from "./PackageTooltip";
import { computeOrderSkidSpaces } from "@src/common/utils/skidSpaceCalculator";
import { SORTED_COLUMN_BG } from "@src/common/lib/tableUtils";
import { ShipmentsTableRowCell } from "./ShipmentsTableRowCell";
import { QualityReviewState } from "./QualityReviewModal/qualityReviewTypes";
import StopStatusCell from "./StopStatusCell";
import { ShipmentStatusCell } from "./ShipmentStatusCell";
import ShipmentsTableDropDown, {
  SHORTCUT_DISPLAY,
} from "../../common/components/ShipmentsTableDropDown";
import { Kbd } from "@src/common/components/Kbd/Kbd";
import RecurringInstanceOrderIcon from "./RecurringInstanceOrderIcon";
import { OrdersPageModalNames } from "../utils/ordersPageModalNames";
import { StopTimeStatusBadge } from "./StopTimeStatusBadge";
import DisplayServiceType from "@src/servicetypes/components/DisplayServiceType";
import {
  ClockIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import RoundTripShipmentLabel from "../RoundTripShipmentIcon";
import MarkShipmentAsClosedButton from "./MarkShipmentAsClosedButton";
import UncloseShipmentButton from "./UncloseShipmentButton";
import { useShipmentSelectionContext } from "../context/ShipmentSelectionContext";
import { ShipmentRow } from "../types";
import { useHeaderSorting } from "@src/common/hooks/useHeaderSorting";
import { useModalsContext } from "../context/ShipmentsTableModalsContext";
import { hoverActions } from "../constants/hoverActions";
import { useShipmentsTableContext } from "../context/ShipmentsTableContext";
import { MoonIcon } from "@heroicons/react/20/solid";
import { PLACEMENT, StatefulTooltip } from "baseui/tooltip";
import moment from "moment";
import { useShipmentFiltersContext } from "../context/ShipmentFiltersContext";
import { Checkbox } from "flowbite-react";
import { hasProposedPackageDetailsToReview } from "../utils/shipmentPackageUtils";
import { getInternalNotePreview } from "../utils/shipmentNoteUtils";
import { DriverNoteIndicator } from "./DriverNoteIndicator";
import { PackageStatusBadges } from "./PackageStatusBadges";
import { resolveOrderIdentifierCell } from "../utils/orderIdentifierColumn";
import { OrderReferenceCell } from "./OrderReferenceCell";
import { OrderGroupLabel } from "@src/orderGroups/components/OrderGroupLabel";
import { useAuth } from "@src/common/hooks/useAuth";
import { effectivePackageTypeNameParts } from "../utils/effectivePackageTypeNames";

type Props = {
  row: ShipmentRow;
  shipmentId: string | undefined;
  /** The courier's SKID category package types; the pallet count shows only for these. */
  skidPackageTypeIds: ReadonlySet<string>;
  orderIdentifierColumnType?: OrderIdentifierColumnType;
  // Shared across rows so shift-click can extend from the last clicked row.
  onToggleCheck: (shipmentId: string, extendRange: boolean) => void;
};

// setting this to prevent column moving when hover buttons are shown
const FIXED_WIDTH_TO_CONTAIN_BOTH_HOVER_BUTTONS_AND_PACKAGEINFO = 100;

function getQualityReviewState(row: ShipmentRow): QualityReviewState {
  const qualityReviews = row?.shipment?.qualityReviews;
  if (!qualityReviews || qualityReviews.length === 0) return QualityReviewState.None;

  const hasBad = qualityReviews.some(
    (review) => review.overallVerdict === QualityReviewVerdict.Bad
  );
  if (hasBad) return QualityReviewState.Bad;

  const hasWarning = qualityReviews.some(
    (review) => review.overallVerdict === QualityReviewVerdict.Warning
  );
  if (hasWarning) return QualityReviewState.Warning;

  const hasGood = qualityReviews.some(
    (review) => review.overallVerdict === QualityReviewVerdict.Good
  );
  if (hasGood) return QualityReviewState.Good;

  return QualityReviewState.None;
}

function OrderRow({
  row,
  shipmentId,
  skidPackageTypeIds,
  orderIdentifierColumnType = OrderIdentifierColumnType.OrderId,
  onToggleCheck,
}: Props) {
  // contexts
  const {
    selectedRow,
    handleSelectRow,
    setShipmentSelectedForAction,
    checkedShipments,
    setSelectedRow,
    setHoveredRow,
    setIsActionPaletteOpen,
    setIsBulkActionPaletteOpen,
    orderIdsClicked,
  } = useShipmentSelectionContext();
  const { areFieldsSorted } = useHeaderSorting();
  const {
    setSelectedArtifactsAtStops,
    setModalOpen,
    setSelectedShipmentIdToUnassign,
    setSelectedShipmentIdPod,
  } = useModalsContext();
  const { minDate, maxDate } = useShipmentFiltersContext();
  const { setSelectedShipmentRowToChangeRouting } = useShipmentsTableContext();
  const { courier } = useAuth();
  const packageTypeNameParts = effectivePackageTypeNameParts(
    row?.shipment?.packageType,
    row?.shipment?.packageItems,
    courier?.courier?.packageTypes ?? [],
  );
  const packageTypeNames: string = packageTypeNameParts
    .map((part) => part.name)
    .join(", ");
  const estimatedSkidSpaces: number | null = computeOrderSkidSpaces(
    row?.shipment?.packageItems,
    row?.shipment?.numPackages,
    row?.shipment?.packageType?.id,
    skidPackageTypeIds,
  );
  // states
  const [lastClosedShipmentIds, setLastClosedShipmentIds] = useState<string[]>(
    []
  );
  const [lastUnclosedShipmentIds, setLastUnclosedShipmentIds] = useState<
    string[]
  >([]);
  const [isHovered, setIsHovered] = useState(false);

  const handleRowMouseEnter = () => {
    setIsHovered(true);
    setHoveredRow(row);
  };
  const handleRowMouseLeave = () => {
    setIsHovered(false);
    setHoveredRow(undefined);
  };

  useEffect(() => {
    if (!row) return;
    if (row.shipment?.order.userHasClicked) return;
    if (!row.shipment) return;
    if (orderIdsClicked.includes(row.shipment.order.id)) return;
  }, [selectedRow]);

  const showDate =
    !minDate || !maxDate || !moment(minDate).isSame(maxDate, "day");
  const showOrderAttachmentsLabel =
    row?.shipment && row.shipment?.order.orderAttachments.length > 0;

  const handleHoverAction = (action) => {
    handleSelectRow(row, () => {
      setShipmentSelectedForAction(row?.shipment);
      switch (action.key) {
        case "viewPod":
          if (row?.artifactsAtStops?.length === 0) return;
          setSelectedArtifactsAtStops(row?.artifactsAtStops || []);
          setSelectedShipmentIdPod(row?.id!);
          setModalOpen(true, OrdersPageModalNames.POD);
          break;
        case "changeRouting":
          if (!row?.canChangeRouting) return;
          setSelectedShipmentRowToChangeRouting(row);
          setModalOpen(true, OrdersPageModalNames.CHANGE_ROUTING);
          break;
        case "unassignDriver":
          if (!row?.canUnassignDriver) return;
          setSelectedShipmentIdToUnassign(row?.id!);
          setModalOpen(true, OrdersPageModalNames.UNASSIGN);
          break;
        case "edit":
          setModalOpen(true, OrdersPageModalNames.EDIT);
          break;
        case "downloadOrderPdf":
          setModalOpen(true, OrdersPageModalNames.PRINT_SHIPPING_LABEL);
          break;
        case "viewOrderAttachments":
          setModalOpen(true, OrdersPageModalNames.ORDER_ATTACHMENTS);
          break;
      }
    });
  };

  const returnActionButtons = () => {
    const actions = hoverActions(row).filter((action) => !action.disabled); // Filter out disabled actions

    return (
      <div
        className={`inset-y-0 flex gap-2 items-center z-10`}
        style={{
          width: "auto",
        }}
      >
        {actions.map((action) => (
          <Tooltip
            key={action.key}
            className="truncate"
            content={
              <span className="flex items-center gap-1.5">
                {action.tooltip || action.label}
                {SHORTCUT_DISPLAY[action.key] && (
                  <Kbd>{SHORTCUT_DISPLAY[action.key]}</Kbd>
                )}
              </span>
            }
          >
            <div
              onClick={() => {
                handleHoverAction(action);
              }}
              color={"secondary"}
              className="hover:bg-slate-300 dark:hover:bg-gray-700 hover:cursor-pointer p-1 rounded-full w-8 h-8 flex items-center justify-center transition-colors duration-150" // Set width and height to 8 and make it flex to center the icon
            >
              {action.icon}
            </div>
          </Tooltip>
        ))}
        <ShipmentsTableDropDown row={row} />
      </div>
    );
  };

  const showUnreadBadge =
    row?.shipment?.order.id &&
    !row?.shipment?.order.userHasClicked &&
    !orderIdsClicked.includes(row?.shipment?.order.id);

  const isProofOfDeliveryMissing = (row: ShipmentRow) => {
    if (row?.shipment?.signedByOverride || row?.shipment?.deliveredAtOverride)
      return false;
    return (
      row?.isCompleted === true &&
      row?.artifactsAtStops &&
      row?.artifactsAtStops.length === 0
    );
  };

  return (
    <StatefulTooltip
      content={() => <div>{returnActionButtons()}</div>}
      returnFocus
      autoFocus
      onMouseEnter={handleRowMouseEnter}
      onMouseLeave={handleRowMouseLeave}
      placement={PLACEMENT.topRight}
      overrides={{
        Body: {
          style: {
            zIndex: 20,
          },
        },
        Inner: {
          style: {
            backgroundColor: "#f0f0f0",
          },
          props: {
            className: "dark:bg-gray-800 dark:border-gray-700",
          },
        },
      }}
      popoverMargin={-22}
    >
      <tr
        style={{ maxWidth: 400 }}
        key={shipmentId}
        onClick={() => {
          handleSelectRow(row);
        }}
        onDoubleClick={(e) => {
          e.preventDefault();
          handleSelectRow(row, () => {
            setShipmentSelectedForAction(row?.shipment);
            setModalOpen(true, OrdersPageModalNames.EDIT);
          });
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          // In bulk mode (shipments checked) open the bulk action palette for
          // the whole selection; otherwise commit the selection (fires
          // dispatch-preview refetch + opens the widget for this row) and open
          // the single-row action palette.
          if (checkedShipments.length > 0) {
            setIsBulkActionPaletteOpen(true);
            return;
          }
          handleSelectRow(row);
          setIsActionPaletteOpen(true);
        }}
        className={classNames({
          "border-b dark:border-gray-700 relative": true,
          "cursor-pointer": selectedRow?.key !== row?.key,
          "bg-blue-100 dark:bg-blue-900/30":
            selectedRow?.key === row?.key ||
            checkedShipments.includes(shipmentId || ""),
          "bg-slate-100 dark:bg-gray-700":
            isHovered && checkedShipments.length === 0,
          "hover:bg-slate-100 dark:hover:bg-gray-700":
            selectedRow?.key !== row?.key && checkedShipments.length === 0,
        })}
        onMouseEnter={handleRowMouseEnter}
        onMouseLeave={handleRowMouseLeave}
      >
        <ShipmentsTableRowCell row={row} selectedRow={selectedRow}>
          <td className="absolute left-2.5 top-5">
            <Checkbox
              checked={
                shipmentId ? checkedShipments.includes(shipmentId) : false
              }
              className="form-checkbox h-5 w-5 mr-2 hover:cursor-pointer"
              onClick={(event) =>
                shipmentId && onToggleCheck(shipmentId, event.shiftKey)
              }
            />
          </td>
        </ShipmentsTableRowCell>
        <ShipmentsTableRowCell
          sortedFields={[ShipmentsSortField.OrderDisplayId]}
          row={row}
          selectedRow={selectedRow}
        >
          {/* The group label sits on its own line outside the centred column, so a wide label
              cannot widen that column and push the order number off the other rows' alignment. */}
          <div className="flex flex-col items-start">
            <div className="flex gap-0.5 items-center">
              {showUnreadBadge && (
                <span className="h-3 w-3 min-w-[12px] min-h-[12px] aspect-square bg-blue-500 rounded-full border border-white"></span>
              )}
              <div className="flex flex-col text-xs items-center">
                <div className="flex gap-1 items-center">
                  {resolveOrderIdentifierCell(row, orderIdentifierColumnType)}
                  {row?.shipment?.internalNote && (
                    <Tooltip
                      content={getInternalNotePreview(
                        row?.shipment?.internalNote
                      )}
                      placement="bottom"
                    >
                      <Badge color="info" className="flex items-center">
                        <DocumentTextIcon className="h-4 w-4" />
                      </Badge>
                    </Tooltip>
                  )}
                </div>
                <div className="flex flex-wrap justify-center items-center">
                  {row?.shipmentType === "ROUND_TRIP" && (
                    <div className="text-xs text-blue-600 dark:text-blue-400">
                      <RoundTripShipmentLabel />
                    </div>
                  )}
                  {row?.recurrenceType === RecurrenceType.RecurringInstance && (
                    <div>
                      <RecurringInstanceOrderIcon />
                    </div>
                  )}
                  {row?.isMultiDay && (
                    <Tooltip content="Multi Day">
                      <MoonIcon className="w-3.5 h-3.5" />
                    </Tooltip>
                  )}
                  {showDate && (
                    <div className="border dark:border-gray-600 truncate p-1 rounded-xl dark:bg-gray-700">
                      <span className="dark:text-gray-300">
                        {moment(row?.shipmentDate).format("MMM DD")}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {row?.shipment?.order.orderGroupOverview && (
              <OrderGroupLabel
                orderGroupDisplayId={
                  row.shipment.order.orderGroupOverview.orderGroupDisplayId
                }
                label={row.shipment.order.orderGroupOverview.label}
                isDispatchable={
                  row.shipment.order.dispatchableType ===
                  DispatchableType.Dispatchable
                }
                onOpen={() => {
                  setShipmentSelectedForAction(row.shipment);
                  setModalOpen(true, OrdersPageModalNames.ORDER_GROUP);
                }}
              />
            )}
          </div>
        </ShipmentsTableRowCell>

        <ShipmentsTableRowCell
          sortedFields={[ShipmentsSortField.EndCustomerName]}
          row={row}
          selectedRow={selectedRow}
          style={{ maxWidth: 220 }}
        >
          <div className="text-xs font-bold truncate dark:text-gray-200">
            {row?.customerDisplayName}
          </div>
          <div className="text-xs">
            {row?.serviceType && (
              <DisplayServiceType
                className="truncate"
                serviceType={row?.serviceType}
                color={row?.serviceTypeColor}
              />
            )}
          </div>
          <div className="font-semibold text-purple-600 dark:text-purple-400 text-xs">
            {row?.shipment?.shipmentTemplate?.order.name}
          </div>
        </ShipmentsTableRowCell>

        <ShipmentsTableRowCell
          sortedFields={[
            ShipmentsSortField.OriginDispatchZone,
            ShipmentsSortField.OriginZip,
            ShipmentsSortField.OriginStreetAddress,
            ShipmentsSortField.OriginCity,
            ShipmentsSortField.PickUpCompanyName,
          ]}
          row={row}
          selectedRow={selectedRow}
        >
          {row?.pickup ? (
            <StopStatusCell
              stopBadge={row?.pickup}
              dispatchZone={row?.origin.dispatchZone}
              dispatchZoneLine2={row?.origin.dispatchZoneLine2}
              isLocationNotFound={row?.origin.isLocationNotFound}
              eta={row?.pickup.eta}
              isLikelyLate={row?.pickup.isLikelyLate}
              taskStatusList={row?.pickup.taskStatusList}
              arrivedAt={row?.pickup.arrivedAt}
              completedAt={row?.pickup.completedAt}
              completedPrefix={
                row?.pickup.completionReason === StopCompletionReason.Attempted
                  ? "Attempted"
                  : "Picked up"
              }
              pickUpStops={row.pickup.pickUpStops}
            />
          ) : (
            <span className="text-xs dark:text-gray-300">-</span>
          )}
        </ShipmentsTableRowCell>

        <ShipmentsTableRowCell
          sortedFields={[
            ShipmentsSortField.DestinationDispatchZone,
            ShipmentsSortField.DestinationZip,
            ShipmentsSortField.DestinationStreetAddress,
            ShipmentsSortField.DestinationCity,
            ShipmentsSortField.DropOffCompanyName,
          ]}
          row={row}
          selectedRow={selectedRow}
        >
          {row?.dropoff ? (
            <StopStatusCell
              stopBadge={row?.dropoff}
              dispatchZone={row?.destination.dispatchZone}
              dispatchZoneLine2={row?.destination.dispatchZoneLine2}
              isLocationNotFound={row?.destination.isLocationNotFound}
              eta={row?.dropoff.eta}
              isLikelyLate={row?.dropoff.isLikelyLate}
              taskStatusList={row?.dropoff.taskStatusList}
              arrivedAt={row?.dropoff.arrivedAt}
              completedAt={row?.dropoff.completedAt}
              completedPrefix={
                row?.dropoff.completionReason === StopCompletionReason.Attempted
                  ? "Attempted"
                  : "Delivered"
              }
              dropOffStops={row.dropoff.dropOffStops}
            />
          ) : (
            <span className="text-xs dark:text-gray-300">-</span>
          )}
        </ShipmentsTableRowCell>

        <ShipmentsTableRowCell
          sortedFields={[ShipmentsSortField.PickUpReadyTime]}
          row={row}
          selectedRow={selectedRow}
        >
          <Table.Cell
            className={classNames({
              "px-2": true,
              [SORTED_COLUMN_BG]: areFieldsSorted([
                ShipmentsSortField.PickUpReadyTime,
              ]),
              "bg-blue-100 dark:bg-blue-900/30": selectedRow?.key === row?.key,
            })}
          >
            <p className="text-xs truncate font-bold mb-0.5 dark:text-gray-300">
              {row?.pickup?.timeWindowText ?? "-"}
            </p>
            {row?.pickup && (
              <StopTimeStatusBadge
                className="w-fit"
                color={row?.pickup.badgeMeta.backgroundColor}
                size={"xs"}
                icon={ClockIcon as React.FC<React.SVGProps<SVGSVGElement>>}
                showBadge={row?.pickup.badgeMeta.shouldShowBadge}
                tooltipContent={row?.pickup.timeWindowText || ""}
              >
                <div className="font-normal truncate text-xs">
                  {row?.pickup.badgeMeta.badgeText}
                </div>
              </StopTimeStatusBadge>
            )}
          </Table.Cell>
        </ShipmentsTableRowCell>
        <ShipmentsTableRowCell
          sortedFields={[ShipmentsSortField.DropOffDueTime]}
          row={row}
          selectedRow={selectedRow}
        >
          <Table.Cell
            className={classNames({
              "px-2 flex flex-col justify-between h-full": true,
              [SORTED_COLUMN_BG]: areFieldsSorted([
                ShipmentsSortField.DropOffDueTime,
                ShipmentsSortField.DropOffReadyTime,
              ]),
              "bg-blue-100 dark:bg-blue-900/30": selectedRow?.key === row?.key,
            })}
          >
            <p className="text-xs truncate font-bold mb-0.5 dark:text-gray-300">
              {row?.dropoff?.timeWindowText ?? "-"}
            </p>
            {row?.dropoff && (
              <StopTimeStatusBadge
                className="w-fit"
                color={row?.dropoff.badgeMeta.backgroundColor}
                size={"xs"}
                icon={ClockIcon as React.FC<React.SVGProps<SVGSVGElement>>}
                showBadge={row?.dropoff.badgeMeta.shouldShowBadge}
                tooltipContent={row?.dropoff.timeWindowText || ""}
              >
                <div className="font-normal text-xs truncate">
                  {row?.dropoff.badgeMeta.badgeText}
                </div>
              </StopTimeStatusBadge>
            )}
          </Table.Cell>
        </ShipmentsTableRowCell>

        <ShipmentsTableRowCell
          sortedFields={[
            ShipmentsSortField.DriverName,
            ShipmentsSortField.Status,
          ]}
          row={row}
          selectedRow={selectedRow}
        >
          <ShipmentStatusCell
            status={row?.status}
            StatusIcon={row?.StatusIcon}
            driverPhoto={row?.driverPhotoUrl}
            driverName={row?.driverName}
            avatarDisplay={row?.driverAvatarDisplay || undefined}
            driver={row?.driverMapItem}
            isCompleted={row?.isCompleted}
            row={row}
          />
          <DriverNoteIndicator shipment={row?.shipment} />
          <PackageStatusBadges
            displayPackageStatuses={row?.shipment?.displayPackageStatuses}
            row={row}
          />
        </ShipmentsTableRowCell>

        <ShipmentsTableRowCell row={row} selectedRow={selectedRow}>
          <div
            className="flex flex-col gap-1 text-xs py-0.5"
            style={{
              width: FIXED_WIDTH_TO_CONTAIN_BOTH_HOVER_BUTTONS_AND_PACKAGEINFO,
            }}
          >
            <div>
              {row?.package && (
                <PackageTooltip
                  pkg={{ ...row.package, estimatedSkidSpaces }}
                />
              )}
            </div>
            <div className="flex items-center gap-1">
              {packageTypeNameParts.length > 0 && (
                <Tooltip content={packageTypeNames} placement="bottom">
                  <div
                    className="truncate mr-1 dark:text-gray-300"
                    style={{ maxWidth: 100 }}
                  >
                    {packageTypeNameParts.map((part, index) => (
                      <span key={part.id}>
                        {index > 0 ? ", " : ""}
                        {part.isShipmentType ? (
                          <span className="font-bold">{part.name}</span>
                        ) : (
                          part.name
                        )}
                      </span>
                    ))}
                  </div>
                </Tooltip>
              )}
              {row?.shipment?.vehicleType && (
                <Tooltip
                  content={`Requested Vehicle Type: ${row.shipment.vehicleType.name}`}
                  placement="bottom"
                >
                  <div className="text-xs dark:text-gray-300">
                    {row.shipment.vehicleType.shortCode}
                  </div>
                </Tooltip>
              )}
            </div>
            {isProofOfDeliveryMissing(row) && (
              <div className="text-xs">
                <div className="flex gap-1">
                  <div className="text-orange-400 truncate">POD Missing</div>
                </div>
              </div>
            )}
            <p className="truncate text-blue-500 font-bold">
              {row?.shipment?.packageDescription}
            </p>
            {showOrderAttachmentsLabel && (
              <p
                className="truncate text-gray-500 dark:text-gray-400 font-bold hover:cursor-pointer hover:underline"
                onClick={() => {
                  setModalOpen(true, OrdersPageModalNames.ORDER_ATTACHMENTS);
                  setShipmentSelectedForAction(row.shipment);
                  setSelectedRow(row);
                }}
              >
                {row.shipment?.order.orderAttachments.length} attachments
              </p>
            )}
            {row?.shipment && hasProposedPackageDetailsToReview(row.shipment) && (
              <Tooltip
                content="Review package detail update"
                placement="bottom"
              >
                <div
                  className="flex items-center gap-1 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md cursor-pointer transition-colors duration-150 w-fit"
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalOpen(
                      true,
                      OrdersPageModalNames.APPROVE_SHIPMENT_DETAILS
                    );
                    setShipmentSelectedForAction(row.shipment);
                    setSelectedRow(row);
                  }}
                >
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                </div>
              </Tooltip>
            )}
          </div>
        </ShipmentsTableRowCell>

        <ShipmentsTableRowCell row={row} selectedRow={selectedRow}>
          <OrderReferenceCell
            referenceNumber={row?.shipment?.order.endCustomerReferenceNumber}
          />
        </ShipmentsTableRowCell>

        <Table.Cell
          className="px-4"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <div className="flex items-center gap-2">
            {row?.canCloseOrder && (() => {
              const reviewState = getQualityReviewState(row);
              
              if (reviewState === QualityReviewState.Bad) {
                return (
                  <Button
                    size="xs"
                    color="failure"
                    onClick={() => {
                      setShipmentSelectedForAction(row.shipment);
                      setSelectedRow(row);
                      setModalOpen(true, OrdersPageModalNames.QUALITY_REVIEW);
                    }}
                  >
                    <div className="flex gap-1 items-center">
                      <ExclamationTriangleIcon className="h-4 w-4" />
                      <span>Review</span>
                    </div>
                  </Button>
                );
              }
              
              if (reviewState === QualityReviewState.Warning) {
                return (
                  <Button
                    size="xs"
                    color="warning"
                    onClick={() => {
                      setShipmentSelectedForAction(row.shipment);
                      setSelectedRow(row);
                      setModalOpen(true, OrdersPageModalNames.QUALITY_REVIEW);
                    }}
                  >
                    <div className="flex gap-1 items-center">
                      <ExclamationTriangleIcon className="h-4 w-4" />
                      <span>Review</span>
                    </div>
                  </Button>
                );
              }
              
              if (reviewState === QualityReviewState.Good) {
                return (
                  <MarkShipmentAsClosedButton
                    onClick={() => {
                      setLastClosedShipmentIds([
                        ...lastClosedShipmentIds,
                        row.id,
                      ]);
                    }}
                    lastClosedShipmentIds={lastClosedShipmentIds}
                    setLastClosedShipmentIds={setLastClosedShipmentIds}
                    row={row}
                    showCheckIcon
                    color="success"
                  />
                );
              }
              
              return (
                <MarkShipmentAsClosedButton
                  onClick={() => {
                    setLastClosedShipmentIds([
                      ...lastClosedShipmentIds,
                      row.id,
                    ]);
                  }}
                  lastClosedShipmentIds={lastClosedShipmentIds}
                  setLastClosedShipmentIds={setLastClosedShipmentIds}
                  row={row}
                />
              );
            })()}
            {row?.canUncloseOrder && (
              <>
                <UncloseShipmentButton
                  onClick={() => {
                    setLastUnclosedShipmentIds([
                      ...lastUnclosedShipmentIds,
                      row.id,
                    ]);
                  }}
                  lastUnclosedShipmentIds={lastUnclosedShipmentIds}
                  setLastUnclosedShipmentIds={setLastClosedShipmentIds}
                  row={row}
                />
              </>
            )}
            <div></div>
          </div>
        </Table.Cell>
      </tr>
    </StatefulTooltip>
  );
}

export default OrderRow;
